import _ from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Reports from "../../common/reports/Reports";
import Autocomplete from "../../framework/Autocomplete";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import Shortcut from "../../framework/Shortcut";
import classes from "./Speciality.module.css";
import SpecialityMasterReportFilter from "./SpecialityMasterReportFilter";
import SpecialityMasterReportSummary from "./SpecialityMasterReportSummary";
import EditSpeciality from "./editSpeciality/index";

const Speciality = () => {
  const [showEditSpeciality, setShowEditSpeciality] = useState(false);
  const [specialityData, setSpecialityData] = useState(null);
  const [onFilterShow, setOnFilterShow] = useState(false);
  const [onColumnShow, setOnColumnShow] = useState(false);
  const [rowLevelCTAs, setRowLevelCTAs] = useState([]);
  const [filterStates, setFilterStates] = useState(null);
  const [currentRowData, setCurrentRowData] = useState(null);
  const { setCurrentComponent, currentComponent } = useContext(GlobalContext);
  const reportRef = useRef();
  const editRef = useRef();
  const refreshRef = useRef();

  const refreshData = useCallback((filterData = {}) => {
    reportRef.current.applyFilters(filterData);
  }, []);

  // Renamed the callback function to avoid conflict
  const handleEditSpeciality = useCallback((isEdit = false) => {
    if (isEdit) {
      const specialityData = reportRef.current.getCurrentRowData();
      setSpecialityData(specialityData);
    }
    setShowEditSpeciality(true);
  }, []);

  const closeEditSpecialityAc = useCallback(() => {
    setShowEditSpeciality(false);
    setSpecialityData(null);
    setCurrentComponent("grid_reports");
  }, [setCurrentComponent]);

  const debounceCloseSpecialityAc = _.debounce(closeEditSpecialityAc, 200);

  const listener = useCallback(
    (e) => {
      const key = e.key?.toLowerCase();
      const ctrlKey = e.ctrlKey;
      const altKey = e.altKey;
      if (altKey && key === "r") {
        refreshRef.current.click();
        return;
      }
      if (ctrlKey && key === "e") {
        if (currentRowData)
          return editRef.current.click();
      }
    },
    [currentRowData]
  );

  useEffect(() => {
    const allRowCTAs = [
      <Button
        ref={editRef}
        startIcon={<Shortcut keys={["Ctrl", "E"]} />}
        label="Edit"
        onClick={() => {
          handleEditSpeciality(true);
        }}
        isAccessible={currentRowData}
      />,
    ];
    setRowLevelCTAs(allRowCTAs);
  }, [currentRowData, handleEditSpeciality]);

  useEffect(() => {
    if (
      !onFilterShow &&
      !showEditSpeciality &&
      !onColumnShow &&
      currentComponent === "grid_reports"
    ) {
      document.addEventListener("keyup", listener, false);
    }
    return () => {
      document.removeEventListener("keyup", listener, false);
    };
  }, [
    currentComponent,
    listener,
    onColumnShow,
    onFilterShow,
    showEditSpeciality,
  ]);

  useEffect(() => {
    refreshData({ isFilterChanged: true, ...filterStates });
  }, [refreshData, filterStates]);

  return (
    <div className={classes["speciality-report-container"]}>
      <Reports
        identifier="specialityMaster"
        reportRef={reportRef}
        rowLevelCTAs={rowLevelCTAs}
        onFilterShowClick={setOnFilterShow}
        onColumnShowClick={setOnColumnShow}
        filters={
          <SpecialityMasterReportFilter
            reportRef={reportRef}
            filters={(filters) => {
              setFilterStates(filters);
            }}
            onFilterShow={onFilterShow}
          />
        }
        currentRow={(data) => {
          setCurrentRowData(data);
        }}
      />
      <SpecialityMasterReportSummary
        refreshData={refreshData}
        refreshRef={refreshRef}
        showAutoComplete={() => {
          handleEditSpeciality();
        }}
      />
      <Autocomplete
        show={showEditSpeciality}
        height="50%"
        width="50%"
        closeAutoComplete={debounceCloseSpecialityAc}
      >
        <EditSpeciality
          specialityData={specialityData}
          onEscape={debounceCloseSpecialityAc}
          onSaveSpeciality={() => {
            debounceCloseSpecialityAc();
            refreshData();
          }}
        />
      </Autocomplete>
    </div>
  );
};

export default Speciality;
